
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrackingHero from './components/TrackingHero';
import TrackingForm from './components/TrackingForm';
import TrackingResult from './components/TrackingResult';
import TrackingEmpty from './components/TrackingEmpty';
import { supabase } from '../../lib/supabase';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

type ViewState = 'idle' | 'loading' | 'found' | 'not-found' | 'error';

export default function OrderTrackingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewState, setViewState] = useState<ViewState>('idle');
  const [order, setOrder] = useState<any>(null);

  useSEO({
    title: 'Suivi de Commande en Temps Réel | Daliah Secret',
    description:
      'Suivez votre commande Daliah Secret en temps réel. Entrez votre numéro de tracking, de commande ou votre adresse email pour voir les mises à jour de livraison.',
    keywords:
      'suivi commande, suivre commande, statut commande, numéro tracking, Daliah Secret, livraison',
    canonical: '/order-tracking',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Suivi de Commande en Temps Réel',
          'Suivez votre commande Daliah Secret en temps réel',
          '/order-tracking'
        ),
        generateBreadcrumbSchema([
          { name: 'Accueil', url: '/' },
          { name: 'Suivi de commande', url: '/order-tracking' },
        ]),
      ],
    },
  });

  const handleSearch = async (query: string, type: 'id' | 'email' | 'tracking') => {
    setViewState('loading');
    setOrder(null);

    try {
      let data: any = null;
      let error: any = null;

      if (type === 'id') {
        const result = await supabase
          .from('orders')
          .select('*')
          .eq('id', query)
          .maybeSingle();
        data = result.data;
        error = result.error;
      } else if (type === 'tracking') {
        const result = await supabase
          .from('orders')
          .select('*')
          .eq('tracking_number', query.trim())
          .maybeSingle();
        data = result.data;
        error = result.error;
      } else {
        const result = await supabase
          .from('orders')
          .select('*')
          .eq('customer_email', query.toLowerCase())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        data = result.data;
        error = result.error;
      }

      if (error) {
        setViewState('not-found');
        return;
      }

      if (data) {
        setOrder(data);
        setViewState('found');
        setSearchParams({ q: query, type });
      } else {
        setViewState('not-found');
      }
    } catch (e) {
      console.error('Search error:', e);
      setViewState('error');
    }
  };

  const handleReset = () => {
    setViewState('idle');
    setOrder(null);
    setSearchParams({});
  };

  useEffect(() => {
    const q = searchParams.get('q');
    const type = searchParams.get('type') as 'id' | 'email' | 'tracking' | null;
    if (q && type && viewState === 'idle') {
      void handleSearch(q, type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFDF7]">
      <Navbar />
      <TrackingHero />

      {(viewState === 'idle' || viewState === 'loading') && (
        <TrackingForm onSearch={handleSearch} loading={viewState === 'loading'} />
      )}

      {viewState === 'found' && order && (
        <TrackingResult order={order} onReset={handleReset} />
      )}

      {(viewState === 'not-found' || viewState === 'error') && (
        <TrackingEmpty type={viewState} onReset={handleReset} />
      )}

      {/* Info cards when idle */}
      {viewState === 'idle' && (
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 p-6 text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-4">
                <i className="ri-barcode-line text-xl text-[#2E7D32]"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Suivi par tracking
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Entrez votre numéro de tracking reçu par email pour suivre votre colis en temps réel.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6 text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-4">
                <i className="ri-refresh-line text-xl text-[#2E7D32]"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Actualisation automatique
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                La page se met à jour automatiquement toutes les 30 secondes pour vous montrer les dernières informations.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6 text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-3-line text-xl text-[#2E7D32]"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Notifications email
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Recevez un email à chaque étape importante : confirmation, expédition et livraison.
              </p>
            </div>
          </div>

          {/* How it works */}
          <div className="mt-16">
            <h2 className="text-center text-xl font-serif text-gray-900 mb-10">
              Comment ça fonctionne ?
            </h2>
            <div className="grid grid-cols-4 gap-4">
              {[
                {
                  step: '1',
                  icon: 'ri-shopping-bag-line',
                  title: 'Commande passée',
                  desc: 'Votre commande est confirmée et enregistrée',
                },
                {
                  step: '2',
                  icon: 'ri-settings-3-line',
                  title: 'Préparation',
                  desc: 'Nos équipes préparent vos produits avec soin',
                },
                {
                  step: '3',
                  icon: 'ri-truck-line',
                  title: 'Expédition',
                  desc: 'Votre colis est remis au transporteur',
                },
                {
                  step: '4',
                  icon: 'ri-home-smile-line',
                  title: 'Livraison',
                  desc: 'Votre commande arrive à votre porte',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#2E7D32] flex items-center justify-center mx-auto mb-3 shadow-lg shadow-[#2E7D32]/20">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-2 text-[10px] font-bold text-[#2E7D32]">
                    {item.step}
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
